import axios, { AxiosResponse } from 'axios';
import {
  AffectedRatePlanModel,
  CoreCacheStatus,
  DiscountRuleFilters,
  DiscountRuleModel,
  ItemPriceFilters,
  ItemPriceModel,
  ItemPriceRequest,
  ListAllRatesParameters,
  OneWayFeeItemPriceModel,
  OneWayFeeItemPriceRequest,
  OneWayFeeModel,
  OneWayFeeRequest,
  OnRequestRuleFilters,
  OnRequestRuleModel,
  OnRequestRuleRequest,
  PriceCalculatorCacheStatus,
  PromoCodeFilters,
  PromoCodeModel,
  RatePlanListAllModel,
  RatePlanModel,
  RatePlanPatchRequest,
  RatePlanPeriodModel,
  RatePlanRequest,
  RatePlanViewComponents,
  RatesOverviewElementModel,
  RatesRequestTestParameters,
  RateStopSellParameters,
  StopSellFilters,
  StopSellModel,
  StopSellsOverviewElementModel,
  StopSellViewContent
} from './pricing.types';
import { GatewayResponse, Page, PageRequest, RevisionModel } from '../api.types';

const FRONT_END_SERVER_API_URL = window.config.apiUrls.frontendServerApi;

const listAllPromoCodes = async (filters?: PromoCodeFilters, page?: PageRequest) => {
  const { data }: AxiosResponse<GatewayResponse<Page<PromoCodeModel>>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/promo-codes`,
    {
      params: {
        combinedFilterField: filters ? filters.combinedFilterField : undefined,
        pickupDate: filters ? filters.pickupDate : undefined,
        requestDate: filters ? filters.requestDate : undefined,
        carClasses: filters ? filters.carClasses : undefined,
        desks: filters ? filters.desks : undefined,
        status: filters ? filters.status : undefined,
        page: filters ? filters.pageNumber : undefined,
        size: 50,
        sortField: page?.sortField,
        sortDirection: page?.sortDirection
      }
    }
  );

  return data;
};

const getOnePromoCode = async (promoCode: string) => {
  const { data }: AxiosResponse<GatewayResponse<PromoCodeModel>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/promo-codes/${promoCode}`
  );

  return data;
};

const getPromoCodeRevisions = async (code: string) => {
  const { data }: AxiosResponse<GatewayResponse<RevisionModel[]>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/promo-codes/${code}/revisions`
  );

  return data;
};

const addNewPromoCode = async (
  request: PromoCodeModel
) => {
  const { data }: AxiosResponse<GatewayResponse<PromoCodeModel>> = await axios.post(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/promo-codes`,
    request
  );

  return data;
};

const editPromoCode = async (
  promoCode: string,
  request: PromoCodeModel
) => {
  const { data }: AxiosResponse<GatewayResponse<PromoCodeModel>> = await axios.put(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/promo-codes/${promoCode}`,
    request
  );

  return data;
};

const deletePromoCode = async (promoCode: string) => {
  await axios.delete(`${FRONT_END_SERVER_API_URL}/:supplier/rates/promo-codes/${promoCode}`);
};

const listAllItemPrices = async (filters?: ItemPriceFilters, page?: PageRequest) => {
  const { data }: AxiosResponse<GatewayResponse<Page<ItemPriceModel>>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/item-prices`,
    {
      params: {
        itemGroupType: filters ? filters.itemGroupType : undefined,
        statusType: filters ? filters.statusType : undefined,
        page: page ? page.page : undefined,
        size: 50,
        sortField: page?.sortField,
        sortDirection: page?.sortDirection
      }
    }
  );
  return data;
};

const listAllItemPricesAllPages = async (filters?: ItemPriceFilters, page?: number) => {
  const { data }: AxiosResponse<GatewayResponse<Page<ItemPriceModel>>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/item-prices`,
    {
      params: {
        itemGroupType: filters ? filters.itemGroupType : undefined,
        size: 500,
        page: page ?? 0
      }
    }
  );

  const content = data.data.content;
  let response: ItemPriceModel[] = [];
  if (!data.data.last) response = await listAllItemPricesAllPages(filters, page ? page + 1 : 1);

  return content.concat(response);
};

const getOneItemPrice = async (id: number) => {
  const { data }: AxiosResponse<GatewayResponse<ItemPriceModel>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/item-prices/${id}`
  );

  return data;
};

const addNewItemPrice = async (
  request: ItemPriceRequest
) => {
  const { data }: AxiosResponse<GatewayResponse<ItemPriceModel>> = await axios.post(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/item-prices`,
    request
  );

  return data;
};

const editItemPrice = async (
  id: number,
  request: ItemPriceRequest
) => {
  const { data }: AxiosResponse<GatewayResponse<ItemPriceModel>> = await axios.put(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/item-prices/${id}`,
    request
  );

  return data;
};

const deleteItemPrice = async (id: number) => {
  await axios.delete(`${FRONT_END_SERVER_API_URL}/:supplier/rates/item-prices/${id}`);
};

const listAllStopSells = async (filters?: StopSellFilters, page?: PageRequest) => {
  const { data }: AxiosResponse<GatewayResponse<Page<StopSellModel>>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/stop-sells`,
    {
      params: {
        carClasses: filters ? filters.carClasses : undefined,
        brokers: filters ? filters.brokers : undefined,
        desks: filters ? filters.desks : undefined,
        ruleType: filters ? filters.ruleType : undefined,
        comment: filters ? filters.comment : undefined,
        date: filters ? filters.date : undefined,
        page: page ? page.page : undefined,
        size: 50,
        sortField: page?.sortField,
        sortDirection: page?.sortDirection
      }
    }
  );

  return data;
};

const getItemPriceRevisions = async (id: number) => {
  const { data }: AxiosResponse<GatewayResponse<RevisionModel[]>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/item-prices/${id}/revisions`
  );

  return data;
};

const getOneStopSell = async (id: number) => {
  const { data }: AxiosResponse<GatewayResponse<StopSellModel>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/stop-sells/${id}`
  );

  return data;
};

const getStopSellRevisions = async (id: number) => {
  const { data }: AxiosResponse<GatewayResponse<RevisionModel[]>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/stop-sells/${id}/revisions`
  );

  return data;
};

const addNewStopSell = async (
  request: StopSellModel
) => {
  const { data }: AxiosResponse<GatewayResponse<StopSellModel>> = await axios.post(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/stop-sells`,
    request
  );

  return data;
};

const editStopSell = async (
  id: number,
  request: StopSellModel
) => {
  const { data }: AxiosResponse<GatewayResponse<StopSellModel>> = await axios.put(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/stop-sells/${id}`,
    request
  );

  return data;
};

const deleteStopSell = async (id: number) => {
  await axios.delete(`${FRONT_END_SERVER_API_URL}/:supplier/rates/stop-sells/${id}`);
};

const listAllRatePlans = async (productId?: number, deskGroupId?: number, carClasses?: string[]) => {
  const { data }: AxiosResponse<GatewayResponse<Page<RatePlanListAllModel>>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/rate-plans`, {
      params: {
        coreProductId: productId,
        deskGroupId,
        carClasses: carClasses?.join(','),
        size: 500
      }
    }
  );
  return data;
};

const getOneRatePlan = async (ratePlanId: number) => {
  const { data }: AxiosResponse<GatewayResponse<RatePlanModel>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/rate-plans/${ratePlanId}`
  );

  return data;
};

const addnewRatePlan = async (
  request: RatePlanRequest
) => {
  const { data }: AxiosResponse<GatewayResponse<RatePlanModel[]>> = await axios.post(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/rate-plans`,
    request
  );

  return data;
};

const editRatePlan = async (
  ratePlanId: number,
  request: RatePlanRequest
) => {
  const { data }: AxiosResponse<GatewayResponse<RatePlanModel>> = await axios.put(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/rate-plans/${ratePlanId}`,
    request
  );

  return data;
};

const patchRatePlan = async (
  ratePlanId: number,
  patchRequest: RatePlanPatchRequest
) => {
  const { data }: AxiosResponse<GatewayResponse<RatePlanModel>> = await axios.patch(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/rate-plans/${ratePlanId}`,
    patchRequest
  );

  return data;
};

const deleteRatePlan = async (ratePlanId: number) => {
  await axios.delete(`${FRONT_END_SERVER_API_URL}/:supplier/rates/rate-plans/${ratePlanId}`);
};

const listAllRatePlanPeriods = async () => {
  const { data }: AxiosResponse<GatewayResponse<Page<RatePlanPeriodModel>>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/rate-plan-periods`
  );

  return data;
};

const listAllRates = async (parameters: ListAllRatesParameters) => {
  const { data }: AxiosResponse<GatewayResponse<RatesOverviewElementModel[]>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/rate-overview`,
    {
      params: {
        startDate: parameters.startDate,
        endDate: parameters.endDate,
        brokerRateCode: parameters.brokerRateCode,
        deskCode: parameters.deskCode,
        groupBy: parameters.groupBy
      }
    }
  );

  return data;
};

const listStopSellsForRates = async (parameters: RateStopSellParameters) => {
  const { data }: AxiosResponse<GatewayResponse<StopSellsOverviewElementModel[]>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/rate-overview/stop-sells`,
    {
      params: {
        startDate: parameters.startDate,
        endDate: parameters.endDate,
        brokerRateCode: parameters.brokerRateCode,
        deskCode: parameters.deskCode
      }
    }
  );

  return data;
};

const getRatePlanViewContent = async (coreProductId?: number) => {
  const { data }: AxiosResponse<GatewayResponse<RatePlanViewComponents>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/rate-plans-view`,
    {
      params: {
        coreProductId: coreProductId ?? undefined
      }
    }
  );

  return data;
};

const getAffectedRatePlans = async (request: RatePlanRequest, id?: number
) => {
  let url = `${FRONT_END_SERVER_API_URL}/:supplier/rates/rate-plans/affected-plans`;
  if (id) {
    url = `${url}/${id}`;
  }
  const { data }: AxiosResponse<GatewayResponse<AffectedRatePlanModel[]>> = await axios.post(
    url,
    request
  );

  return data;
};

const getStopSellsViewContent = async () => {
  const { data }: AxiosResponse<GatewayResponse<StopSellViewContent>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/stop-sells-view`
  );

  return data;
};

const listAllOneWayFees = async (pageNumber: number): Promise<OneWayFeeModel[]> => {
  const { data }: AxiosResponse<GatewayResponse<Page<OneWayFeeModel>>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/one-way-fees`,
    {
      params: {
        page: pageNumber,
        size: 500
      }
    }
  );

  const content = data.data.content;
  let response: OneWayFeeModel[] = [];
  if (!data.data.last) {
    response = await listAllOneWayFees(pageNumber + 1);
  }

  return content.concat(response);
};

const addOneWayFees = async (oneWayFees: OneWayFeeRequest[]) => {
  const { data }: AxiosResponse<GatewayResponse<OneWayFeeModel[]>> = await axios.post(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/one-way-fees`, oneWayFees
  );

  return data;
};

const editOneWayFee = async (oneWayFees: OneWayFeeRequest) => {
  const url = `${FRONT_END_SERVER_API_URL}/:supplier/rates/one-way-fees`;
  await axios.post(url, oneWayFees);
};

const deleteOneWayFee = async (id: number) => {
  await axios.delete(`${FRONT_END_SERVER_API_URL}/:supplier/rates/one-way-fees/${id}`);
};

const getOneWayFeesItemPrice = async () => {
  const { data }: AxiosResponse<GatewayResponse<OneWayFeeItemPriceModel>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/one-way-fees/item-price`
  );

  return data;
};

const editOneWayFeesItemPrice = async (oneWayFeesItemPrice: OneWayFeeItemPriceRequest) => {
  const { data }: AxiosResponse<GatewayResponse<OneWayFeeItemPriceModel>> = await axios.put(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/one-way-fees/item-price`, oneWayFeesItemPrice
  );

  return data;
};

const getOneWayFeeRevisions = async (pickupDeskCode: string, returnDeskCode: string) => {
  const { data }: AxiosResponse<GatewayResponse<RevisionModel[]>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/one-way-fees/revisions`,
    {
      params: {
        pickupDeskCode: pickupDeskCode,
        returnDeskCode: returnDeskCode
      }
    }
  );

  return data;
};

const getRateCacheStatus = async () => {
  const { data }: AxiosResponse<GatewayResponse<PriceCalculatorCacheStatus>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/cache-status/price-calculator`
  );

  return data;
};

const getCoreRateCacheStatus = async () => {
  const { data }: AxiosResponse<GatewayResponse<CoreCacheStatus>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/cache-status/core`
  );

  return data;
};

const publishRatePlans = async () => {
  await axios.post(`${FRONT_END_SERVER_API_URL}/:supplier/rates/rate-plans/publish`);
};

const listAllOnRequestRules = async (filters?: OnRequestRuleFilters, page?: PageRequest) => {
  console.log(page);
  const { data }: AxiosResponse<GatewayResponse<Page<OnRequestRuleModel>>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/on-request-rules`,
    {
      params: {
        carClass: filters ? filters.carClass : undefined,
        desk: filters ? filters.desk : undefined,
        page: page ? page.page : undefined,
        size: 50,
        sortField: page?.sortField,
        sortDirection: page?.sortDirection
      }
    }
  );

  return data;
};

const getOnRequestRule = async (id: number) => {
  const { data }: AxiosResponse<GatewayResponse<OnRequestRuleModel>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/on-request-rules/${id}`
  );

  return data;
};

const addNewOnRequestRule = async (
  request: OnRequestRuleRequest
) => {
  const { data }: AxiosResponse<GatewayResponse<OnRequestRuleModel>> = await axios.post(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/on-request-rules`,
    request
  );

  return data;
};

const getOnRequestRuleRevisions = async (id: number) => {
  const { data }: AxiosResponse<GatewayResponse<RevisionModel[]>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/on-request-rules/${id}/revisions`
  );

  return data;
};

const editOnRequestRule = async (
  id: number,
  request: OnRequestRuleRequest
) => {
  const { data }: AxiosResponse<GatewayResponse<OnRequestRuleModel>> = await axios.put(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/on-request-rules/${id}`,
    request
  );

  return data;
};

const deleteOnRequestRule = async (id: number) => {
  await axios.delete(`${FRONT_END_SERVER_API_URL}/:supplier/rates/on-request-rules/${id}`);
};

const listAllDiscountRules = async (filters?: DiscountRuleFilters, page?: PageRequest) => {
  const { data }: AxiosResponse<GatewayResponse<Page<DiscountRuleModel>>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/discount-rules`,
    {
      params: {
        pickupDate: filters ? filters.pickupDate : undefined,
        desk: filters ? filters.desks : undefined,
        broker: filters ? filters.brokers : undefined,
        page: page ? page.page : undefined,
        size: 50,
        sortField: page?.sortField,
        sortDirection: page?.sortDirection
      }
    }
  );

  return data;
};

const listAllDiscountRulesAllPages = async (pageNumber: number): Promise<DiscountRuleModel[]> => {
  const { data }: AxiosResponse<GatewayResponse<Page<DiscountRuleModel>>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/discount-rules`,
    {
      params: {
        page: pageNumber,
        size: 20
      }
    }
  );

  const content = data.data.content;
  let response: DiscountRuleModel[] = [];
  if (!data.data.last) {
    response = await listAllDiscountRulesAllPages(pageNumber + 1);
  }

  return content.concat(response);

};

const getDiscountRule = async (id: number) => {
  const { data }: AxiosResponse<GatewayResponse<DiscountRuleModel>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/discount-rules/${id}`
  );

  return data;
};

const getDiscountRuleRevisions = async (id: number) => {
  const { data }: AxiosResponse<GatewayResponse<RevisionModel[]>> = await axios.get(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/discount-rules/${id}/revisions`
  );

  return data;
};

const addNewDiscountRule = async (
  request: DiscountRuleModel
) => {
  const { data }: AxiosResponse<GatewayResponse<DiscountRuleModel>> = await axios.post(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/discount-rules`,
    request
  );

  return data;
};

const editDiscountRule = async (
  id: number,
  request: DiscountRuleModel
) => {
  const { data }: AxiosResponse<GatewayResponse<DiscountRuleModel>> = await axios.put(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/discount-rules/${id}`,
    request
  );

  return data;
};

const deleteDiscountRule = async (id: number) => {
  await axios.delete(`${FRONT_END_SERVER_API_URL}/:supplier/rates/discount-rules/${id}`);
};

const makeRatesRequestTest = async (parameters: RatesRequestTestParameters) => {
  const { data }: AxiosResponse<GatewayResponse<string>> = await axios.post(
    `${FRONT_END_SERVER_API_URL}/:supplier/rates/rates-request-test`, parameters
  );

  return data;
};

const api = {
  PromoCodes: {
    listAllPromoCodes,
    getOnePromoCode,
    addNewPromoCode,
    editPromoCode,
    deletePromoCode,
    getPromoCodeRevisions
  },
  ItemPrices: {
    listAllItemPrices,
    listAllItemPricesAllPages,
    getOneItemPrice,
    addNewItemPrice,
    editItemPrice,
    deleteItemPrice,
    getItemPriceRevisions
  },
  RatePlan: {
    listAllRatePlans,
    getOneRatePlan,
    addnewRatePlan,
    editRatePlan,
    deleteRatePlan,
    patchRatePlan,
    listAllRatePlanPeriods,
    getRatePlanViewContent,
    publishRatePlans,
    getAffectedRatePlans
  },
  StopSells: {
    listAllStopSells,
    getOneStopSell,
    editStopSell,
    addNewStopSell,
    deleteStopSell,
    getStopSellsViewContent,
    getStopSellRevisions
  },
  RatesOverview: {
    listAllRates,
    listStopSellsForRates
  },
  OneWayFees: {
    listAllOneWayFees,
    addOneWayFees,
    editOneWayFee,
    deleteOneWayFee,
    editOneWayFeesItemPrice,
    getOneWayFeesItemPrice,
    getOneWayFeeRevisions
  },
  cacheStatus: {
    getRateCacheStatus,
    getCoreRateCacheStatus
  },
  OnRequestRules: {
    listAllOnRequestRules,
    getOnRequestRule,
    addNewOnRequestRule,
    editOnRequestRule,
    deleteOnRequestRule,
    getOnRequestRuleRevisions
  },
  DiscountRules: {
    listAllDiscountRules,
    listAllDiscountRulesAllPages,
    getDiscountRule,
    addNewDiscountRule,
    editDiscountRule,
    deleteDiscountRule,
    getDiscountRuleRevisions
  },
  RatesRequestTest: {
    makeRatesRequestTest
  }
};

export default api;
